/**
 * CORE-880 — как строка истории покупок превращается в то, что видит человек.
 *
 * Вынесено из компонента отдельным модулем без зависимостей от React Native:
 * здесь живут решения (что показать, что скрыть, куда можно перейти), и их
 * нужно уметь проверять тестами, а не глазами. Под jest в этом репозитории
 * компоненты RN не импортируются — правила, спрятанные внутри JSX, остались бы
 * непокрытыми.
 *
 * Ничего не считает. Суммы, валюта и срок — снимки, взятые покупкой в момент
 * оформления (§6 ТЗ), и пересчитывать их по сегодняшней цене программы нельзя:
 * человек купил то, что ему показали. Здесь только выбор слов.
 */
import type { PurchaseHistoryItem } from '../../types/programs';

/**
 * Тон строки. Не цвет: цвета живут в теме и различаются в светлой и тёмной,
 * а тон — это то, чем строка является, и он одинаков всюду.
 */
export type PurchaseTone = 'active' | 'pending' | 'muted' | 'failed';

export interface PurchaseView {
  /** Главный ярлык строки — одной фразой, что с этой покупкой. */
  statusLabel: string;
  tone: PurchaseTone;
  /** Вторая строка: срок доступа или причина неудачи. Пусто, когда сказать нечего. */
  note: string;
  /** Можно ли открыть купленное. */
  canOpen: boolean;
}

const MONTHS = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

/**
 * Дата покупки словами. Год печатается всегда: история живёт годами, и
 * «14 марта» без года в ней двусмысленно.
 */
export function formatPurchaseDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * Дата, до которой действует доступ.
 *
 * Отдельно от {@link formatPurchaseDate} намеренно: у покупки день, а у срока —
 * день с оговоркой «до», и склеивать их одной функцией значит однажды написать
 * «доступ 14 марта».
 */
export function formatAccessUntil(item: PurchaseHistoryItem): string {
  if (item.accessUnlimited) return 'Доступ без ограничения по времени';
  if (!item.accessExpiresAtUtc) return '';
  const when = formatPurchaseDate(item.accessExpiresAtUtc);
  return when ? `Доступ до ${when}` : '';
}

/**
 * Что показать по строке истории.
 *
 * Пары «платёж × доступ» разбираются вместе, а не двумя независимыми ярлыками.
 * Оплаченная покупка с истёкшим сроком — это не «оплачено» и не «истекло»
 * порознь: человеку важно единственное — работает ли ещё то, за что он заплатил.
 */
export function describePurchase(item: PurchaseHistoryItem): PurchaseView {
  switch (item.paymentStatus) {
    case 'pending':
      return {
        statusLabel: 'Платёж в обработке',
        tone: 'pending',
        // Ни сроков, ни обещаний: подтверждение приходит от платёжной системы,
        // и назвать время, которым мы не управляем, значит однажды соврать.
        note: 'Доступ откроется, как только оплата подтвердится',
        canOpen: false,
      };

    case 'failed':
      return {
        statusLabel: 'Платёж не прошёл',
        tone: 'failed',
        // Причина от платёжного контура, если он её назвал. Своего текста не
        // придумываем: человеку нужно то, что скажет ему банк, а не наша догадка.
        note: item.failureReason || 'Деньги не списаны — покупку можно повторить',
        canOpen: false,
      };

    case 'refunded':
      return {
        statusLabel: 'Деньги возвращены',
        tone: 'muted',
        note: 'Доступ к программе закрыт',
        canOpen: false,
      };

    case 'paid':
      if (item.accessStatus === 'expired') {
        return {
          statusLabel: 'Доступ завершён',
          tone: 'muted',
          note: item.accessExpiresAtUtc
            ? `Действовал до ${formatPurchaseDate(item.accessExpiresAtUtc)}`
            : '',
          canOpen: false,
        };
      }
      return {
        statusLabel: 'Доступ активен',
        tone: 'active',
        note: formatAccessUntil(item),
        canOpen: true,
      };

    default:
      // Неизвестный статус — не повод прятать строку: покупка была, деньги
      // двигались. Показываем её нейтрально, без выдуманного объяснения.
      return { statusLabel: 'Покупка', tone: 'muted', note: '', canOpen: false };
  }
}

/**
 * Что делает эта покупка — программа или отдельная практика.
 *
 * В истории лежат оба продукта вперемешку, и без ярлыка «Медитация утра» за
 * 900 ₽ неотличима от программы за те же деньги.
 */
export function productLabel(item: PurchaseHistoryItem): string {
  return item.productType === 'paidPractice' ? 'Практика' : 'Программа';
}

/**
 * Стоит ли вообще рисовать раздел.
 *
 * Пустая история — не пустое состояние, а отсутствие раздела: человеку, который
 * ничего не покупал, незачем объяснять, что он ничего не покупал. Раздел
 * появится сам, когда появится первая покупка.
 */
export function shouldRenderSection(totalCount: number): boolean {
  return totalCount > 0;
}

/**
 * Короткая сводка над списком.
 *
 * Показывается только когда сообщает новое. Одна покупка описывает себя сама
 * своей же строкой, а «платежей в обработке: 0» — шум.
 */
export function summaryLine(totalCount: number, pendingCount: number): string {
  if (totalCount <= 1 && pendingCount === 0) return '';
  const parts: string[] = [];
  if (totalCount > 1) parts.push(`${totalCount} ${pluralPurchases(totalCount)}`);
  if (pendingCount > 0) parts.push(`${pendingCount} в обработке`);
  return parts.join(' · ');
}

function pluralPurchases(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'покупка';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'покупки';
  return 'покупок';
}
